import { useEffect, useRef, useState } from "react";
import { Play, Pause } from "lucide-react";
import { fileUrl } from "../lib/api";

const resolveSrc = (p) => (p && /^https?:\/\//i.test(p) ? p : (p ? fileUrl(p) : undefined));

export default function ABAudioPlayer({ title = "Untitled", artist = "", beforePath, afterPath, cover }) {
  const [mode, setMode] = useState("after"); // 'before' | 'after'
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const beforeRef = useRef(null);
  const afterRef = useRef(null);

  const getRef = () => (mode === "before" ? beforeRef.current : afterRef.current);
  const otherRef = () => (mode === "before" ? afterRef.current : beforeRef.current);

  useEffect(() => {
    const el = getRef();
    if (!el) return;
    if (playing) el.play().catch(() => setPlaying(false));
    else el.pause();
    if (otherRef()) otherRef().pause();
    // sync playhead
    if (otherRef() && el) el.currentTime = otherRef().currentTime || 0;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, playing]);

  useEffect(() => {
    const el = getRef();
    if (!el) return;
    const onT = () => setProgress(el.duration ? (el.currentTime / el.duration) * 100 : 0);
    el.addEventListener("timeupdate", onT);
    return () => el.removeEventListener("timeupdate", onT);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const toggle = () => setPlaying((p) => !p);

  const bars = Array.from({ length: 60 }, (_, i) => Math.abs(Math.sin(i * 0.35)) * 40 + 8);

  return (
    <div className="panel p-5 md:p-6 fade-up" data-testid="ab-audio-player">
      <div className="flex items-start gap-4">
        {cover && (
          <img src={fileUrl(cover)} alt="" className="w-16 h-16 rounded-lg object-cover border border-white/5" />
        )}
        <div className="flex-1 min-w-0">
          <div className="mono-caps">A/B Karşılaştırma</div>
          <h3 className="font-display text-lg md:text-xl font-semibold text-white truncate">{title}</h3>
          <div className="text-sm text-slate-400 truncate">{artist}</div>
        </div>
        <div className="flex rounded-full border border-white/10 overflow-hidden text-xs font-mono">
          <button
            data-testid="ab-toggle-before"
            onClick={() => setMode("before")}
            className={`px-3 py-2 uppercase tracking-widest ${mode === "before" ? "bg-amber-400 text-black" : "text-slate-300"}`}
          >
            Before
          </button>
          <button
            data-testid="ab-toggle-after"
            onClick={() => setMode("after")}
            className={`px-3 py-2 uppercase tracking-widest ${mode === "after" ? "bg-cyan-400 text-black" : "text-slate-300"}`}
          >
            After
          </button>
        </div>
      </div>

      <div className="mt-5 flex items-center gap-4">
        <button
          onClick={toggle}
          data-testid="ab-play-toggle"
          className="w-12 h-12 rounded-full flex items-center justify-center bg-cyan-400 text-black hover:bg-cyan-300 transition-colors flex-shrink-0 shadow-lg shadow-cyan-500/20"
          aria-label="play"
        >
          {playing ? <Pause className="w-5 h-5" fill="currentColor" /> : <Play className="w-5 h-5 ml-0.5" fill="currentColor" />}
        </button>
        <div className="flex-1 h-16 flex items-end gap-[2px] overflow-hidden">
          {bars.map((h, i) => {
            const active = (i / bars.length) * 100 < progress;
            return (
              <span
                key={i}
                style={{
                  height: h,
                  background: active ? (mode === "before" ? "#FFB800" : "#00E5FF") : "#2a3040",
                  transition: "background 120ms ease",
                }}
                className="flex-1 rounded-sm"
              />
            );
          })}
        </div>
      </div>

      <audio ref={beforeRef} src={resolveSrc(beforePath)} preload="none" onEnded={() => setPlaying(false)} />
      <audio ref={afterRef} src={resolveSrc(afterPath)} preload="none" onEnded={() => setPlaying(false)} />
    </div>
  );
}
